/**
 * 习惯行为指数 V2（P1-1.7 表现层 + P1-1.8 方法抽屉）
 *
 * 三件事，按出错代价排序：
 *  1. **两端点编排与一致性守卫（D14）**：判定来自 `/healthHabit/v2/profile`，逐日序列来自
 *     `/healthHabit/indicators`（42 天）。两请求同窗口同基准，任一失败或两边对不上 ⇒ 整页降级，
 *     不做"半新半旧"—— 数都对、故事是错的，页面上一切正常，这是最贵的一类 bug。
 *  2. **窗口只有一个来源**：`resolveHabitWindow({windowDays:42, alignCompleteWeeks:true})`（D15）。
 *     本文件不出现 moment，也不自己算日期（P6 锚点混用的复发点）。
 *  3. **只画，不判**（D12/R16）：档位、充分性、模式、桶码全部来自载荷；这里允许的比较只有
 *     字符串相等与"键在不在"。数值缩放只在 scale.js 一处。
 *
 * 迟到响应：切用户时上一份响应可能后到，用请求序号丢弃它 —— 不然 A 用户的分数会画在 B 用户页上。
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Spin } from 'antd';
import { getHealthHabitProfile } from '@/services/healthHabitProfile';
import { getHealthHabitIndicators } from '@/services/healthHabit';
import { resolveHabitWindow, V2_WINDOW_DAYS } from '../window';
import { trackUnknownBuckets } from '../tracking';
import { checkProfile, checkWindowConsistency } from './guard';
import {
  DIMENSION_ORDER,
  STAGE_SECTIONS,
  STAGE_HEADLINE,
  isPageLevelBucket,
  KNOWN_BUCKET_CODES,
  isRegisteredBucket,
} from './registry';
import { pageContext, metaLine, PAGE_COPY } from './copy';
import { NoticeList } from './Notice';
import DimensionCard from './DimensionCard';
import OverviewRow from './OverviewRow';
import MethodDrawer from './MethodDrawer';
import styles from './index.less';

/**
 * 降级归因只有三个码（handoff §3.2）：CONTRACT_INVALID / UPSTREAM_ERROR / UPSTREAM_EMPTY。
 *
 * 为什么不把 guard 的细原因（UNKNOWN_MODE、NDAYS_MISMATCH…）直接递上去：那些是**诊断**，
 * 不是**归类**；页面把内部原因码当用户可见的归因，下游报表就会每期多一个枚举值。
 * 细原因另走埋点（trackUnknownBuckets 同一通道），降级只说“哪一类坏了”。
 */
function degradeReasonFor(reason) {
  if (reason === 'UPSTREAM_EMPTY') return 'UPSTREAM_EMPTY';
  // 网络层失败也是"上游没给回东西"，不是契约问题 —— 归反了会让人去查前端版本而不是查服务
  if (reason === 'NETWORK_ERROR' || reason === 'UPSTREAM_ERROR') {
    return 'UPSTREAM_ERROR';
  }
  return 'CONTRACT_INVALID';
}

/** indicators 的响应归一：$x 已剥掉 axios 那层，这里面对的是 {code,msg,data} */
function readIndicators(res) {
  if (res === null || res === undefined) {
    return { ok: false, reason: 'UPSTREAM_EMPTY', data: null };
  }
  if (typeof res !== 'object' || Array.isArray(res)) {
    return { ok: false, reason: 'CONTRACT_INVALID', data: null };
  }
  const code = res.code === undefined ? null : String(res.code);
  if (code !== null && code !== '00000') {
    return { ok: false, reason: 'UPSTREAM_ERROR', data: null, code };
  }
  const data = res.data !== undefined ? res.data : res;
  if (!data || typeof data !== 'object') {
    return { ok: false, reason: 'UPSTREAM_EMPTY', data: null, code };
  }
  return { ok: true, reason: null, data, code };
}

/** 载荷里出现、但本页没有模板的码：已登记的（P2 提前上线）与完全未知的都算，埋点里能分开看 */
function collectUnrendered(dims) {
  const codes = [];
  DIMENSION_ORDER.forEach((key) => {
    const buckets = Array.isArray(dims[key] && dims[key].buckets)
      ? dims[key].buckets
      : [];
    buckets.forEach((code) => {
      if (KNOWN_BUCKET_CODES.indexOf(code) === -1) codes.push(code);
    });
  });
  return Array.from(new Set(codes));
}

/** 跨维口径（common.*）在页面顶部只说一次，别在四张卡片里各说一遍 */
function commonCodesOf(dims) {
  const codes = [];
  DIMENSION_ORDER.forEach((key) => {
    const buckets = Array.isArray(dims[key] && dims[key].buckets)
      ? dims[key].buckets
      : [];
    buckets.forEach((code) => {
      // 用 isPageLevelBucket 而不是裸的 'common.' 前缀：common.noData 带这个前缀，
      // 说的却是某一维的事 —— 提到页顶就成了没主语的"这一维…"（见 registry 里的注释）
      if (isPageLevelBucket(code)) codes.push(code);
    });
  });
  return Array.from(new Set(codes));
}

/** dims 的固定空对象：避免 profile 为空时每次渲染都造一个新引用，把 useMemo 打穿 */
const EMPTY_DIMS = {};

/**
 * 逐日事实清单在**每维的 dim.facts** 里（页顶没有 facts），所以 S0 的"覆盖了多少天"走页头 meta
 * 那一句 + 四张定义卡。若以后服务端补上页级 facts，再在这里加一个条，不预先按不存在的键写代码。
 */
export default function HealthHabitV2View({ userId, onDegrade }) {
  // 窗口在组件生命周期内恒定：翻页/重渲染不该换锚点
  const win = React.useMemo(
    () =>
      resolveHabitWindow({
        windowDays: V2_WINDOW_DAYS,
        // 42 天截止昨天（2026-09-16 拍板）。旧口径退到"最近一个过完的周日"，
        // 会把本周已发生的记录整段丢在窗外（实测：某会员 09-14 的睡眠在页面上根本不存在）。
        // 只退一天就够：当天数据天然不完整，昨天及以前都是完整的自然日。
        endAtYesterday: true,
      }),
    [],
  );
  const [state, setState] = React.useState({ phase: 'loading' });
  const [methodOpen, setMethodOpen] = React.useState(false);
  const requestSeq = React.useRef(0);
  // onDegrade 在外壳每次渲染都是新函数：进 deps 就是"渲染→effect→setState→渲染"的死循环
  const degradeRef = React.useRef(onDegrade);
  degradeRef.current = onDegrade;

  const degrade = React.useCallback((reason) => {
    setState({ phase: 'degraded', reason });
    if (degradeRef.current) degradeRef.current(reason);
  }, []);

  React.useEffect(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setState({ phase: 'loading' });

    const profileRequest = getHealthHabitProfile({
      requestData: {
        userId,
        windowDays: win.windowDays,
        asOfDate: win.periodEnd,
      },
    }).catch(() => ({ ok: false, reason: 'UPSTREAM_ERROR', data: null }));

    const indicatorsRequest = getHealthHabitIndicators({
      requestData: {
        userId,
        periodStart: win.periodStart,
        periodEnd: win.periodEnd,
      },
    })
      .then(readIndicators)
      .catch(() => ({ ok: false, reason: 'UPSTREAM_ERROR', data: null }));

    Promise.all([profileRequest, indicatorsRequest]).then(
      ([profileRes, indicatorsRes]) => {
        if (seq !== requestSeq.current) return; // 迟到响应丢弃（切用户时的串台防线）
        if (!profileRes.ok) {
          degrade(degradeReasonFor(profileRes.reason));
          return;
        }
        if (!indicatorsRes.ok) {
          degrade(degradeReasonFor(indicatorsRes.reason));
          return;
        }
        if (!profileRes.data) {
          degrade('UPSTREAM_EMPTY');
          return;
        }
        const profile = profileRes.data;
        const indicators = indicatorsRes.data;
        const shape = checkProfile(profile);
        if (!shape.ok) {
          degrade(degradeReasonFor(shape.reason));
          return;
        }
        const consistency = checkWindowConsistency({
          profile,
          indicators,
          window: {
            windowDays: win.windowDays,
            periodStart: win.periodStart,
            periodEnd: win.periodEnd,
          },
        });
        if (!consistency.ok) {
          degrade(degradeReasonFor(consistency.reason));
          return;
        }
        setState({ phase: 'ready', profile, indicators });
      },
    );

    return () => {
      requestSeq.current = seq + 1; // 卸载/换用户：让在途响应落地时被认成过期
    };
  }, [userId, win, degrade]);

  // 所有 hook 必须在提前返回之前（ready 之前 dims 取空对象），否则加载→就绪时 hook 数量变了
  const profile = state.phase === 'ready' ? state.profile : null;
  const indicators = state.indicators;
  const dims = React.useMemo(
    () => (profile && profile.dimensions) || EMPTY_DIMS,
    [profile],
  );
  const unrendered = React.useMemo(() => collectUnrendered(dims), [dims]);
  const commonCodes = React.useMemo(() => commonCodesOf(dims), [dims]);
  // V1 第一行的时段偏好就在 indicators 里（同一份载荷已经有，不用额外请求）
  const sportPreferences =
    (indicators && indicators.sport && indicators.sport.activePreferences) ||
    [];
  const engineVersion = profile && profile.engineVersion;

  /**
   * 未登记/未启用的桶码上报（忽略 + 继续渲染，不崩页也不降级）。
   * 完全未知的码和已登记但本期不展的码一起发：埋点里能分开看"服务端上新了"还是"我们漏接了"。
   * 放在渲染前的副作用里而不是 render 体内：上报会 setState（sa-sdk 内部），不能在渲染中跑。
   */
  React.useEffect(() => {
    if (!profile || !unrendered.length) return;
    const unknown = unrendered.filter((code) => !isRegisteredBucket(code));
    const deferred = unrendered.filter((code) => isRegisteredBucket(code));
    trackUnknownBuckets({
      codes: unknown.concat(deferred),
      dimension: null,
      userId,
      engineVersion,
    });
  }, [unrendered.join('|'), userId, engineVersion]);

  if (state.phase === 'loading') {
    return (
      <div className={`${styles.v2} ${styles.loading}`}>
        <Spin />
      </div>
    );
  }

  if (state.phase !== 'ready') {
    // 外壳随即把整页降回 V1；这里只留一行，避免降级瞬间两页堆叠
    return (
      <div className={styles.v2}>
        <Alert
          type="warning"
          showIcon
          message={PAGE_COPY.degradeHint}
          description={state.reason || ''}
        />
      </div>
    );
  }

  const pageCtx = pageContext(profile);
  const sections = STAGE_SECTIONS[profile.stage] || STAGE_SECTIONS.S1;
  // 固定四张卡（2026-09-16 拍板）：不再按 stage / 有没有数据挑着显示。
  // 缺数据的那张卡自己会说清"没有记录"——比"这张卡不存在"更好解释，也才看得见"哪一维是空的"。
  const cardDims = DIMENSION_ORDER;

  return (
    <div className={styles.v2} data-test="hh-v2-root">
      <div className={styles.header}>
        <div>
          <div className={styles.meta}>{metaLine(pageCtx)}</div>
        </div>
        <Button
          size="small"
          onClick={() => setMethodOpen(true)}
          data-test="hh-v2-method"
        >
          {PAGE_COPY.methodTrigger}
        </Button>
      </div>

      <NoticeList buckets={commonCodes} ctx={pageCtx} />

      {/*
       * V1 第一行（2026-09-16 拍板：永久保留）——指数仪表盘 / 时段偏好 / 近 6 周趋势。
       * 它自己拉 /healthHabit/index（上周口径），拉不到就退化成文字，不影响下面四张卡。
       */}
      <OverviewRow
        userId={userId}
        preferences={sportPreferences}
        windowDays={win.windowDays}
      />

      {/* 去掉总分（2026-09-16 拍板）：不再合成分，只看四维各自的分。
          覆盖度这一句留着 —— 它是"这些数是几天算出来的"，不读它就不知道分数的分量。 */}
      <div className={styles.coverage} data-test="hh-v2-coverage">
        <span className={styles.coverageLabel}>{PAGE_COPY.stageLabel}</span>
        <span>{STAGE_HEADLINE[profile.stage] || profile.stage}</span>
      </div>

      {/* 观察窗说明挪到这里（2026-09-17）：它讲的是“下面这些数是怎么截出来的”，紧贴四张卡才顺；
          “数据覆盖”那句管的是服务端判定的充分性，两句是一对。放页顶时，它和它描述的数字
          中间隔着 V1 第一行，读者接不上。 */}
      <div className={styles.windowNote}>{PAGE_COPY.windowNote}</div>

      {/* 总：先回答"这个会员这段怎么样" —— 固定四张维度卡 */}
      <div className={styles.grid}>
        {cardDims.map((key) => (
          <DimensionCard
            key={key}
            dimension={key}
            dim={dims[key]}
            indicators={indicators}
            win={win}
            pageCtx={pageCtx}
            sections={sections}
          />
        ))}
      </div>

      {/* 指标图表已经并进上面四张维度卡（2026-09-16 拍板），这里不再挂独立的趋势区 */}

      <div className={styles.footer}>
        {profile.mode === 'SINGLE'
          ? PAGE_COPY.footerSingle
          : PAGE_COPY.footerDefault}
      </div>

      <MethodDrawer visible={methodOpen} onClose={() => setMethodOpen(false)} />
    </div>
  );
}

/**
 * 未展示码只走埋点，不在页面上开一行：运营不需要知道“服务端今天上新了规则”。
 * 但埋点不能漏 —— 看不见的映射缺失就是永久看不见的映射缺失。
 */
HealthHabitV2View.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // 外壳注入：契约非法 / 上游失败时整页降回 V1（§6）
  onDegrade: PropTypes.func.isRequired,
};
